// src/services/driverSessionService.js
import { db, collection, doc, getDocs, addDoc, updateDoc, query, where } from './firebase';
import { serverTimestamp } from 'firebase/firestore';

const toDateKey = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const y = d.getFullYear();
  return `${m}-${day}-${y}`;
};

// Journey stages in order
export const JOURNEY_STAGES = [
  { id: 'idle',        label: 'Not Started',           icon: '⏳', color: '#9ca3af' },
  { id: 'left_home',   label: 'Left Home',              icon: '🏠', color: '#f59e0b' },
  { id: 'left_center', label: 'Left Center',            icon: '🚐', color: '#3b82f6' },
  { id: 'at_school',   label: 'At School / Picking Up', icon: '🏫', color: '#8b5cf6' },
  { id: 'en_route',    label: 'En Route to Stemtree',   icon: '🚗', color: '#f97316' },
  { id: 'arrived',     label: 'Arrived at Stemtree',    icon: '✅', color: '#10b981' },
];

export const getStageInfo = (stageId) =>
  JOURNEY_STAGES.find(s => s.id === stageId) || JOURNEY_STAGES[0];

// Get or create today's session for a driver
export const getOrCreateSession = async (driver) => {
  const date = toDateKey();
  try {
    const q = query(
      collection(db, 'driver_sessions'),
      where('driverId', '==', driver.id),
      where('date', '==', date)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      return { id: snap.docs[0].id, ...snap.docs[0].data() };
    }
    const newSession = {
      driverId: driver.id,
      driverName: driver.name,
      driverPhone: driver.phone || '',
      date,
      journeyStatus: 'idle',
      lastUpdatedAt: serverTimestamp(),
      studentsTotal: 0,
      studentsPickedUp: 0,
      studentsDelivered: 0,
    };
    const ref = await addDoc(collection(db, 'driver_sessions'), newSession);
    return { id: ref.id, ...newSession };
  } catch (err) {
    console.error('Error getting/creating driver session:', err);
    throw err;
  }
};

// Update journey status
export const updateJourneyStatus = async (sessionId, status, studentCounts = null) => {
  try {
    const update = {
      journeyStatus: status,
      lastUpdatedAt: serverTimestamp(),
    };
    if (studentCounts) {
      update.studentsTotal = studentCounts.total;
      update.studentsPickedUp = studentCounts.pickedUp;
      update.studentsDelivered = studentCounts.delivered;
    }
    await updateDoc(doc(db, 'driver_sessions', sessionId), update);
  } catch (err) {
    console.error('Error updating journey status:', err);
    throw err;
  }
};

// Update student counts on a session
export const updateSessionCounts = async (sessionId, counts) => {
  try {
    await updateDoc(doc(db, 'driver_sessions', sessionId), {
      studentsTotal: counts.total,
      studentsPickedUp: counts.pickedUp,
      studentsDelivered: counts.delivered,
      lastUpdatedAt: serverTimestamp(),
    });
  } catch (err) {
    console.error('Error updating session counts:', err);
  }
};

// Get all active sessions for today (for admin monitor)
export const getTodaysSessions = async () => {
  const date = toDateKey();
  try {
    const q = query(collection(db, 'driver_sessions'), where('date', '==', date));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('Error fetching today sessions:', err);
    return [];
  }
};

/**
 * Calculate when a driver must leave home and leave Stemtree
 * to reach each school before the earliest dismissal time.
 *
 * @param {Object} driver - driver doc with driveTimes map
 * @param {Array} pickupStudents - today's pickup students assigned to this driver
 * @returns {Array} thresholds: [{ schoolName, dismissalTime, mustLeaveHome, mustLeaveCenter, isLate }]
 */
export const calculateLateThresholds = (driver, pickupStudents) => {
  const isWednesday = new Date().getDay() === 3;
  const driveTimes = driver.driveTimes || {};
  const homeToStemtree = driver.homeToStemtree || null;

  // Group students by school, find earliest dismissal per school
  const schoolMap = {};
  pickupStudents.forEach(student => {
    const school = student.schoolName;
    if (!school) return;
    const rawTime = isWednesday
      ? (student.dismissalTimeWednesday || student.dismissalTimeNormal)
      : student.dismissalTimeNormal;
    if (!rawTime) return;
    const [h, m] = rawTime.split(':').map(Number);
    const mins = h * 60 + m;
    if (!schoolMap[school] || mins < schoolMap[school].dismissalMins) {
      schoolMap[school] = { schoolName: school, dismissalMins: mins, dismissalDisplay: formatMins(mins) };
    }
  });

  const nowMins = new Date().getHours() * 60 + new Date().getMinutes();

  return Object.values(schoolMap).map(school => {
    const dt = driveTimes[school.schoolName] || {};
    const fromStemtree = dt.fromStemtree || null;

    // Must leave Stemtree: dismissal - driveToSchool - 10 min buffer
    const mustLeaveCenterMins = fromStemtree !== null
      ? school.dismissalMins - fromStemtree - 10
      : null;

    // Must leave home: must leave center - driveHomeToStemtree - 5 min buffer
    const mustLeaveHomeMins = (mustLeaveCenterMins !== null && homeToStemtree !== null)
      ? mustLeaveCenterMins - homeToStemtree - 5
      : null;

    return {
      schoolName: school.schoolName,
      dismissalTime: school.dismissalDisplay,
      mustLeaveHome: mustLeaveHomeMins !== null ? formatMins(mustLeaveHomeMins) : null,
      mustLeaveCenter: mustLeaveCenterMins !== null ? formatMins(mustLeaveCenterMins) : null,
      mustLeaveHomeMins,
      mustLeaveCenterMins,
      isLateFromHome: mustLeaveHomeMins !== null && nowMins > mustLeaveHomeMins,
      isLateFromCenter: mustLeaveCenterMins !== null && nowMins > mustLeaveCenterMins,
    };
  }).sort((a, b) => (a.mustLeaveHomeMins || 999) - (b.mustLeaveHomeMins || 999));
};

// Format minutes since midnight → "3:15 PM"
const formatMins = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
};